import React, { useState, useEffect, useRef, useCallback } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Upload, FileText, Trash2, UploadCloud, BookOpen } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { documentsApi } from '../../utils/api';

const CATEGORIES = [
  { value: 'agrement_pmi', label: 'Agrément PMI', color: 'bg-navy-100 text-navy-700', icon: '🏛️' },
  { value: 'protocole_sante', label: 'Protocole santé', color: 'bg-green-100 text-green-700', icon: '🏥' },
  { value: 'protocole_securite', label: 'Protocole sécurité', color: 'bg-red-100 text-red-700', icon: '🔒' },
  { value: 'legislation', label: 'Législation', color: 'bg-navy-100 text-navy-700', icon: '⚖️' },
  { value: 'procedure_interne', label: 'Procédure interne', color: 'bg-orange-100 text-orange-700', icon: '📋' },
  { value: 'autre', label: 'Autre', color: 'bg-slate-100 text-slate-600', icon: '📄' },
];

function getCatInfo(value) {
  return CATEGORIES.find(c => c.value === value) || CATEGORIES[4];
}

function formatSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function CategoryBadge({ value }) {
  const cat = getCatInfo(value);
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cat.color}`}>{cat.label}</span>;
}

function UploadTab({ structures, onUploaded }) {
  const [dragging, setDragging] = useState(false);
  const [files, setFiles] = useState([]);
  const [nom, setNom] = useState('');
  const [categorie, setCategorie] = useState('');
  const [structureId, setStructureId] = useState('');
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('fichier'); // 'fichier' | 'dossier'
  const fileRef = useRef();
  const folderRef = useRef();

  const ALLOWED = ['.pdf', '.docx', '.doc', '.txt'];

  const filterFiles = (fileList) => {
    return Array.from(fileList).filter(f => {
      const ext = f.name.slice(f.name.lastIndexOf('.')).toLowerCase();
      return ALLOWED.includes(ext);
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = filterFiles(e.dataTransfer.files);
    if (dropped.length) { setFiles(dropped); setError(null); }
    else setError('Aucun fichier compatible (PDF, DOCX, TXT)');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!files.length) { setError('Veuillez sélectionner un fichier ou un dossier'); return; }
    if (!categorie) { setError('Veuillez choisir une catégorie'); return; }
    setError(null);

    if (files.length === 1) {
      setProgress('Envoi en cours...');
      try {
        const fd = new FormData();
        fd.append('fichier', files[0]);
        fd.append('nom', nom || files[0].name.replace(/\.[^.]+$/, ''));
        fd.append('categorie', categorie);
        if (structureId) fd.append('structure_id', structureId);
        await documentsApi.upload(fd);
        setProgress('✅ Document ajouté !');
        setFiles([]); setNom('');
        onUploaded();
        setTimeout(() => setProgress(null), 3000);
      } catch { setError("Erreur lors de l'envoi"); setProgress(null); }
    } else {
      // Plusieurs fichiers (dossier)
      setProgress(`Envoi de ${files.length} fichiers...`);
      try {
        const fd = new FormData();
        files.forEach(f => fd.append('fichiers', f));
        fd.append('categorie', categorie);
        if (structureId) fd.append('structure_id', structureId);
        const res = await documentsApi.uploadMultiple(fd);
        setProgress(`✅ ${res.data.count} document(s) ajouté(s) !`);
        setFiles([]);
        onUploaded();
        setTimeout(() => setProgress(null), 4000);
      } catch { setError("Erreur lors de l'envoi du dossier"); setProgress(null); }
    }
  };

  const inputClass = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white';
  const labelClass = 'block text-xs font-medium text-slate-600 mb-1';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Mode selector */}
      <div className="flex rounded-lg border border-slate-200 overflow-hidden text-sm">
        <button type="button" onClick={() => { setMode('fichier'); setFiles([]); }}
          className={`flex-1 py-2 font-medium transition-colors ${mode === 'fichier' ? 'bg-primary-50 text-primary-700' : 'text-slate-500 hover:bg-slate-50'}`}>
          📄 Fichier unique
        </button>
        <button type="button" onClick={() => { setMode('dossier'); setFiles([]); }}
          className={`flex-1 py-2 font-medium transition-colors ${mode === 'dossier' ? 'bg-primary-50 text-primary-700' : 'text-slate-500 hover:bg-slate-50'}`}>
          📁 Dossier entier
        </button>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => mode === 'dossier' ? folderRef.current?.click() : fileRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${dragging ? 'border-primary-400 bg-primary-50' : 'border-slate-200 hover:border-slate-300 bg-slate-50'}`}
      >
        <input type="file" ref={fileRef} className="hidden" accept=".pdf,.docx,.txt"
          onChange={e => { const f = filterFiles(e.target.files); if (f.length) { setFiles(f); setNom(f[0].name.replace(/\.[^.]+$/, '')); } }} />
        <input type="file" ref={folderRef} className="hidden" webkitdirectory="true" multiple
          onChange={e => { const f = filterFiles(e.target.files); if (f.length) setFiles(f); }} />
        <UploadCloud size={36} className={`mx-auto mb-2 ${dragging ? 'text-primary-500' : 'text-slate-300'}`} />
        {files.length > 0 ? (
          <div>
            <p className="text-sm font-medium text-slate-700">
              {files.length === 1 ? files[0].name : `${files.length} fichiers sélectionnés`}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {files.length > 1 ? files.map(f => f.name).slice(0, 3).join(', ') + (files.length > 3 ? '...' : '') : formatSize(files[0].size)}
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-500">
              {mode === 'dossier' ? 'Cliquer pour sélectionner un dossier' : 'Glisser-déposer ou parcourir'}
            </p>
            <p className="text-xs text-slate-400 mt-1">PDF, DOCX, TXT{mode === 'dossier' ? ' · Tous les fichiers du dossier seront importés' : ' · max 50 Mo'}</p>
          </>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        {files.length <= 1 && (
          <div>
            <label className={labelClass}>Nom du document</label>
            <input type="text" value={nom} onChange={e => setNom(e.target.value)} className={inputClass} placeholder="Ex: Agrément PMI 2024" />
          </div>
        )}
        <div className={files.length > 1 ? 'col-span-2' : ''}>
          <label className={labelClass}>Catégorie *</label>
          <select value={categorie} onChange={e => setCategorie(e.target.value)} className={inputClass} required>
            <option value="">— Choisir —</option>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.icon} {c.label}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className={labelClass}>Structure (optionnel)</label>
        <select value={structureId} onChange={e => setStructureId(e.target.value)} className={inputClass}>
          <option value="">Vue générale</option>
          {structures.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
        </select>
      </div>

      {/* Tip agrément PMI */}
      {categorie === 'agrement_pmi' && (
        <div className="bg-navy-50 border border-navy-200 rounded-lg px-3 py-2 text-xs text-navy-700 space-y-1">
          <p>🏛️ <strong>Agrément PMI :</strong> Georges pourra s'y référer pour le taux d'encadrement, la capacité autorisée et les conditions spécifiques à votre structure.</p>
          <p>⚠️ <strong>Important :</strong> sélectionnez bien la structure concernée ci-dessus — chaque crèche a son propre agrément PMI.</p>
        </div>
      )}

      {error && <p className="text-red-500 text-xs">{error}</p>}
      {progress && <p className="text-green-600 text-xs font-medium">{progress}</p>}

      <button type="submit" className="w-full bg-primary-500 hover:bg-primary-600 text-white py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2">
        <Upload size={16} /> {files.length > 1 ? `Importer ${files.length} fichiers` : 'Téléverser le document'}
      </button>
    </form>
  );
}

function TexteTab({ structures, onUploaded }) {
  const [nom, setNom] = useState('');
  const [categorie, setCategorie] = useState('');
  const [structureId, setStructureId] = useState('');
  const [contenu, setContenu] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nom || !categorie || !contenu) { setError('Tous les champs sont requis'); return; }
    setSaving(true); setError(null);
    try {
      await documentsApi.addTexte({ nom, categorie, structure_id: structureId || null, contenu });
      setNom(''); setCategorie(''); setStructureId(''); setContenu('');
      setSuccess(true); onUploaded();
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      setError('Erreur lors de l\'enregistrement');
    } finally { setSaving(false); }
  };

  const inputClass = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white';
  const labelClass = 'block text-xs font-medium text-slate-600 mb-1';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Nom *</label>
          <input type="text" value={nom} onChange={e => setNom(e.target.value)} className={inputClass} required />
        </div>
        <div>
          <label className={labelClass}>Catégorie *</label>
          <select value={categorie} onChange={e => setCategorie(e.target.value)} className={inputClass} required>
            <option value="">— Choisir —</option>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className={labelClass}>Structure (optionnel)</label>
        <select value={structureId} onChange={e => setStructureId(e.target.value)} className={inputClass}>
          <option value="">Vue générale</option>
          {structures.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
        </select>
      </div>
      <div>
        <label className={labelClass}>Contenu *</label>
        <textarea value={contenu} onChange={e => setContenu(e.target.value)} rows={8} className={inputClass} placeholder="Collez votre texte ici..." required />
      </div>
      {error && <p className="text-red-500 text-xs">{error}</p>}
      {success && <p className="text-green-600 text-xs font-medium">Document ajouté avec succès !</p>}
      <button type="submit" disabled={saving} className="w-full bg-primary-500 hover:bg-primary-600 text-white py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-60">
        {saving ? 'Enregistrement...' : 'Ajouter le texte'}
      </button>
    </form>
  );
}

export default function DocumentsPage() {
  const { structures, getApiParams } = useApp();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('fichier');
  const [deleting, setDeleting] = useState(null);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await documentsApi.getAll(getApiParams());
      setDocuments(res.data || []);
    } catch {
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [getApiParams]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    setDeleting(id);
    try {
      await documentsApi.delete(id);
      setDocuments(prev => prev.filter(d => d.id !== id));
    } catch {
      // silent
    } finally {
      setDeleting(null);
    }
  };

  const grouped = CATEGORIES.reduce((acc, cat) => {
    const docs = documents.filter(d => d.categorie === cat.value);
    if (docs.length) acc.push({ cat, docs });
    return acc;
  }, []);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-xl bg-navy-100 flex items-center justify-center">
            <BookOpen size={18} className="text-navy-700" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Bibliothèque de documents</h1>
        </div>
        <p className="text-slate-500 text-sm ml-12">Ces documents sont utilisés par Georges IA pour répondre aux questions et générer des conseils adaptés à votre structure.</p>
      </div>

      {/* Upload card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-8">
        <div className="flex border-b border-slate-100">
          <button
            onClick={() => setTab('fichier')}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${tab === 'fichier' ? 'text-primary-600 border-b-2 border-primary-500' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <Upload size={15} /> Téléverser un fichier
          </button>
          <button
            onClick={() => setTab('texte')}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${tab === 'texte' ? 'text-primary-600 border-b-2 border-primary-500' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <FileText size={15} /> Ajouter du texte
          </button>
        </div>
        <div className="p-5">
          {tab === 'fichier' ? (
            <UploadTab structures={structures} onUploaded={loadDocs} />
          ) : (
            <TexteTab structures={structures} onUploaded={loadDocs} />
          )}
        </div>
      </div>

      {/* Document list */}
      <div>
        <h2 className="text-base font-semibold text-slate-700 mb-4">Documents chargés</h2>

        {loading ? (
          <div className="text-center py-10 text-slate-400">Chargement...</div>
        ) : documents.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-dashed border-slate-200">
            <BookOpen size={36} className="mx-auto mb-3 text-slate-200" />
            <p className="text-slate-400 text-sm">Aucun document. Chargez vos protocoles pour activer Georges IA.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {grouped.map(({ cat, docs }) => (
              <div key={cat.value}>
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${cat.color}`}>{cat.label}</span>
                  <span className="text-xs text-slate-400">{docs.length} document{docs.length > 1 ? 's' : ''}</span>
                </div>
                <div className="space-y-2">
                  {docs.map(doc => (
                    <div key={doc.id} className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-4 py-3 shadow-sm">
                      <FileText size={18} className="text-slate-300 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{doc.nom}</p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {formatSize(doc.taille)}
                          {doc.created_at ? ` · ${format(new Date(doc.created_at), 'd MMM yyyy', { locale: fr })}` : ''}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDelete(doc.id)}
                        disabled={deleting === doc.id}
                        className="p-1.5 text-slate-300 hover:text-red-400 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40"
                        title="Supprimer"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
